// QA doc: features/website/cart.md R-C35 (a page left open across a deploy asks for a lazy file that is gone: the page is loaded at the address
// asked for, once, before any dialog), R-C35a (a lazy file that failed once but is still on the host is loaded again the same way).
import { DOCUMENT, Injectable, inject } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { BrowserWindow } from '@core/misc/browser-window.service';
import { ConfirmService } from '@shared/modules/confirm/confirm.service';
import { chunkLoadErrorMessage, chunkUrlOf, isChunkLoadError, mainScriptOf } from './stale-bundle.utils';

/** Client error codes of this refusal (cart.md error table): no request reaches any server, so there is no Trace ID. */
const STALE_BUNDLE_CODE = 'APP_UPDATED';
const CHUNK_LOAD_FAILED_CODE = 'CHUNK_LOAD_FAILED';
const CHUNK_RETRY_CODE = 'CHUNK_RETRY';
/** session-storage record of the one new page load made for a failed lazy file (R-C35, R-C35a) */
const RETRY_KEY = 'staleBundle.retry';
const RETRY_WINDOW_MS = 2 * 60 * 1000;

/**
 * A tab that stays open across a deploy keeps the old `main` file, whose lazy files (the checkout page among them) no longer exist on the host.
 * The router then fails the navigation and, without this service, the agent saw nothing at all: "Checkout does not take me to the registration
 * page" (Amiran, 12.10.2026, the Nha Trang cart).
 *
 * The page is loaded once more at the address asked for, without a dialog (Chrome keeps a failed module import for the life of the page, a router
 * retry fails at once - DEV 18.10): after a deploy that load takes the new build (23.10.2026: TEST was deployed 11 times and STG 6 times in a day,
 * and the blocking "The site has been updated" dialog met the agents after each), and a file that failed once on the way (R-C35a, run 26
 * WEB-26-10) is asked again. The cart lives in session storage and survives the load (cart.md R-C17). Only a second failure of the same address
 * within RETRY_WINDOW_MS opens a dialog: "The site has been updated" when the host holds another build (the failed file answers 404 / 410, or,
 * when the browser does not name it, index.html names another `main`), else a file that could not be loaded.
 */
@Injectable()
export class StaleBundleService {
  readonly #router = inject(Router);
  readonly #confirmService = inject(ConfirmService);
  readonly #window = inject(BrowserWindow);

  readonly #document = inject(DOCUMENT);

  #isDialogOpen = false;

  public init(): void {
    this.#router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) this.#forgetRetry(event.url);
      else if (event instanceof NavigationError && isChunkLoadError(event.error))
        void this.#onChunkLoadError(event.url, event.error);
    });
  }

  async #onChunkLoadError(url: string, error: unknown): Promise<void> {
    const deployed = await this.#newBuildDeployed(error);
    if (this.#startRetry(url)) {
      console.warn(
        deployed
          ? `[StaleBundle] ${STALE_BUNDLE_CODE}: a lazy file of this page is gone after a deploy - the page is loaded at the address asked for; url=${url}; error=${chunkLoadErrorMessage(error)}`
          : `[StaleBundle] ${CHUNK_RETRY_CODE}: a lazy file of this page could not be loaded but the build is unchanged - the page is loaded once more; url=${url}; error=${chunkLoadErrorMessage(error)}`
      );
      this.#window.location.assign(url);
      return;
    }
    this.#forgetRetry(url);
    if (deployed) this.#announce(url, error);
    else this.#announceNotLoaded(url, error);
  }

  /** Records the one new load of `url` (R-C35a); false when that load was made already (within RETRY_WINDOW_MS) or the tab cannot record it. */
  #startRetry(url: string): boolean {
    try {
      const last = JSON.parse(this.#window.sessionStorage.getItem(RETRY_KEY) ?? 'null') as {
        url: string;
        at: number;
      } | null;
      if (last?.url === url && Date.now() - last.at < RETRY_WINDOW_MS) return false;
      this.#window.sessionStorage.setItem(RETRY_KEY, JSON.stringify({ url, at: Date.now() }));
      return true;
    } catch {
      return false;
    }
  }

  #forgetRetry(url: string): void {
    try {
      const last = JSON.parse(this.#window.sessionStorage.getItem(RETRY_KEY) ?? 'null') as { url: string } | null;
      if (last?.url === url) this.#window.sessionStorage.removeItem(RETRY_KEY);
    } catch {
      // nothing recorded
    }
  }

  /** true: the host holds another build (the failed file is gone, or index.html names another `main`); false: same build or unknown. */
  async #newBuildDeployed(error: unknown): Promise<boolean> {
    try {
      const file = chunkUrlOf(error);
      if (file) {
        const answer = await this.#window.fetch(file, { method: 'HEAD', cache: 'no-store' });
        return answer.status === 404 || answer.status === 410;
      }
      const current = this.#document.querySelector('script[src*="main-"]')?.getAttribute('src') ?? null;
      const index = await this.#window.fetch(this.#document.baseURI, { cache: 'no-store' });
      const latest = index.ok ? mainScriptOf(await index.text()) : null;
      return !!current && !!latest && current !== latest;
    } catch {
      return false;
    }
  }

  #announceNotLoaded(url: string, error: unknown): void {
    console.error(
      `[StaleBundle] ${CHUNK_LOAD_FAILED_CODE}: a lazy file of this page could not be loaded twice, the build is unchanged; url=${url}; error=${chunkLoadErrorMessage(error)}`
    );
    this.#openDialog(
      'This step could not be opened',
      'A file of this page could not be loaded. Reload the page - your cart stays as it is.'
    );
  }

  #announce(url: string, error: unknown): void {
    console.error(
      `[StaleBundle] ${STALE_BUNDLE_CODE}: a lazy file of this page is gone after a deploy and the new page load did not reach it; url=${url}; error=${chunkLoadErrorMessage(error)}`
    );
    this.#openDialog(
      'The site has been updated',
      'A newer version of the site was released while this page was open, so this step could not be opened. Reload the page - your cart stays as it is.'
    );
  }

  #openDialog(title: string, message: string): void {
    if (this.#isDialogOpen) return;
    this.#isDialogOpen = true;

    this.#confirmService
      .confirm(
        { title, message },
        { type: 'warn' },
        {
          primaryButton: {
            label: 'Reload page',
            handler: () => {
              this.#window.location.reload();
            }
          }
        }
      )
      .subscribe(() => {
        this.#isDialogOpen = false;
      });
  }
}
